import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/components/ui/use-toast';
import { hasSignificantClockDrift, getClockDriftMinutes } from '@/lib/serverTime';
import {
  persistSessionIfRemembered,
  loadPersistedSessionTokens,
  clearPersistedSession,
} from '@/lib/auth/sessionPersistence';
import { getStableDeviceId } from '@/lib/auth/deviceId';
import { logAuthEvent, markBackgrounded, consumeBackgroundedMs } from '@/lib/debug/authEventLog';


interface UserProfile {
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  user_type: string | null;
  subscription_active: boolean;
  documents_submitted: boolean;
  shipper_approved: boolean | null;
  fmcsa_verified: boolean | null;
  fmcsa_legal_name: string | null;
  fmcsa_dot_number: string | null;
  fmcsa_mc_number: string | null;
  is_demo_user: boolean;
  lr_direct_request_status: string | null;
  lr_direct_requested_at: string | null;
  avatar_url: string | null;
  phone: string | null;
  phone_verified: boolean | null;
  notification_email: boolean | null;
  notification_sms_enabled: boolean | null;
  notification_push_enabled: boolean | null;
  notification_load_alerts: any | null;
  notification_messages: any | null;
  notification_rate_alerts: any | null;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: UserProfile | null;
  loading: boolean;
  hasActiveSubscription: boolean;
  isDemoUser: boolean;
  isAdmin: boolean;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  profile: null,
  loading: true,
  hasActiveSubscription: false,
  isDemoUser: false,
  isAdmin: false,
  refreshProfile: async () => {},
});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Extract session_id from JWT payload - this is stable across token refreshes
const extractSessionId = (accessToken: string): string | null => {
  try {
    const parts = accessToken.split('.');
    if (parts.length !== 3) return null;

    // base64url -> base64 (JWT uses base64url)
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + (4 - (base64.length % 4)) % 4, '=');

    const payload = JSON.parse(atob(padded));
    return payload?.session_id ?? null;
  } catch {
    return null;
  }
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [companySubActive, setCompanySubActive] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  
  const isSigningOut = useRef(false);
  const currentSessionId = useRef<string | null>(null);
  const currentUserId = useRef<string | null>(null);
  const channelRef = useRef<any>(null);
  const profileChannelRef = useRef<any>(null);
  const fetchUserProfileRef = useRef<((userId: string, opts?: { isRefresh?: boolean }) => Promise<void>) | null>(null);
  const driftCheckedRef = useRef(false);

  const refreshProfile = async () => {
    if (user?.id && fetchUserProfileRef.current) {
      // isRefresh: merge new row into existing profile in place — never null
      // out consumers between old and new, and don't toggle the top-level
      // `loading` flag. Prevents dashboards from collapsing to a blank state
      // while any refresh is in flight (e.g. after signup / doc submit /
      // avatar update / billing return / FMCSA verify).
      await fetchUserProfileRef.current(user.id, { isRefresh: true });
    }
  };
  const pollingRef = useRef<NodeJS.Timeout | null>(null);
  const mountedRef = useRef(true);
  

  useEffect(() => {
    mountedRef.current = true;


    const cleanup = () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
      if (profileChannelRef.current) {
        supabase.removeChannel(profileChannelRef.current);
        profileChannelRef.current = null;
      }
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };

    const forceSignOut = async (message: string) => {
      if (isSigningOut.current || !mountedRef.current) return;
      isSigningOut.current = true;

      logAuthEvent('force_signout', {
        reason: message,
        session_id: currentSessionId.current,
      }, currentUserId.current);



      // Cleanup subscriptions first
      cleanup();
      currentSessionId.current = null;
      currentUserId.current = null;
      // Persist a "flash" message for /auth (hard redirect wipes in-page toasts)
      try {
        sessionStorage.setItem(
          'lr_auth_flash_v1',
          JSON.stringify({
            title: 'Signed Out',
            description: message,
        variant: 'destructive',
        duration: 30000,
          })
        );
      } catch {
        // ignore
      }

      // Clear state immediately
      setUser(null);
      setSession(null);
      setProfile(null);

      try {
        // Forced sign-out: always invalidate the persisted "Remember Me" tokens
        // so the next cold boot does not silently restore this account.
        clearPersistedSession();
        // Sign out from Supabase (sessionStorage-based, so this tab only)
        await supabase.auth.signOut({ scope: 'local' });
      } catch (e) {
        console.error('Error during signOut:', e);
      }

      // Best-effort toast (may not render before redirect)
      toast({
        title: 'Signed Out',
        description: message,
            variant: 'destructive',
            duration: 30000,
      });

      // Force redirect to auth page
      setTimeout(() => {
        window.location.href = '/auth?mode=login';
      }, 50);

      setTimeout(() => {
        isSigningOut.current = false;
      }, 1000);
    };

    // Check that MY session_id row still exists. With multi-device, other
    // rows may exist for the same user; we only care whether ours was revoked.
    const checkSessionValid = async (): Promise<boolean> => {
      if (!currentSessionId.current || !currentUserId.current || !mountedRef.current) {
        return true; // Don't invalidate if we don't have session info
      }

      try {
        const { data, error } = await supabase
          .from('active_sessions')
          .select('session_id')
          .eq('user_id', currentUserId.current)
          .eq('session_id', currentSessionId.current)
          .maybeSingle();

        if (error) {
          console.error('Session check error:', error);
          return true; // Don't invalidate on error
        }
        return !!data;
      } catch (e) {
        console.error('Session validation error:', e);
        return true;
      }
    };

    const registerAndMonitorSession = async (sess: Session) => {
      if (!mountedRef.current || isSigningOut.current) return;
      
      const regStart = performance.now();
      const sessionId = extractSessionId(sess.access_token);
      if (!sessionId) {
        console.error('Could not extract session_id from token');
        return;
      }
      
      const userId = sess.user.id;
      
      // Skip if already registered this exact session
      if (currentSessionId.current === sessionId && currentUserId.current === userId) {
        return;
      }
      
      cleanup();
      
      currentSessionId.current = sessionId;
      currentUserId.current = userId;
      
      // Register session as a per-device row. UPSERT on (user_id, device_id)
      // — re-login on THIS device replaces only its own row. Other devices
      // (phone/PC) are left intact so multi-device login is allowed.
      const deviceId = await getStableDeviceId();
      console.log(`[AUTH-TIMING] register_user_session RPC start (device ${deviceId.slice(-8)})`);
      const { error } = await supabase.rpc('register_user_session', {
        p_session_id: sessionId,
        p_device_id: deviceId,
        p_user_agent: navigator.userAgent,
      });
      console.log(`[AUTH-TIMING] register_user_session RPC done in ${(performance.now() - regStart).toFixed(0)}ms`);
      
      if (error) {
        console.error('Error registering session:', error);
        return;
      }
      
      console.log('Session registered:', sessionId.slice(-8), 'device:', deviceId.slice(-8));
      // Realtime subscription for THIS user's sessions — DELETE only.
      // We intentionally do NOT listen for INSERT: another device signing in
      // is now legitimate and must not force-sign-out this device. A DELETE
      // of MY session_id is still an explicit revoke (future revoke flow /
      // 5-device cap eviction / admin) and correctly signs this device out.
      channelRef.current = supabase
        .channel(`session-${userId.slice(-8)}`)
        .on(
          'postgres_changes',
          {
            event: 'DELETE',
            schema: 'public',
            table: 'active_sessions',
            filter: `user_id=eq.${userId}`
          },
          (payload) => {
            if (!mountedRef.current || isSigningOut.current) return;
            
            const deletedSessionId = (payload.old as any)?.session_id;
            console.log('Session deleted event:', deletedSessionId?.slice(-8), 'Current:', currentSessionId.current?.slice(-8));
            
            if (deletedSessionId === currentSessionId.current) {
              forceSignOut("You were signed out because your session was revoked.");
            }
          }
        )
        .subscribe(async (status) => {
          console.log('Session channel status:', status);
          // Self-heal on (re)subscribe: a DELETE that arrived during a
          // CHANNEL_ERROR gap would otherwise be lost. Re-verify our
          // session row still exists; if not, force sign-out.
          if (status === 'SUBSCRIBED') {
            const stillValid = await checkSessionValid();
            if (!stillValid && mountedRef.current && !isSigningOut.current) {
              forceSignOut("You were signed out because your session was revoked.");
            }
          }
        });
      
      // Realtime subscription for profile changes (Fix 3: access kill on subscription loss)
      if (profileChannelRef.current) {
        supabase.removeChannel(profileChannelRef.current);
      }
      profileChannelRef.current = supabase
        .channel(`profile-sub-${userId.slice(-8)}`)
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'profiles',
            filter: `id=eq.${userId}`,
          },
          (payload) => {
            if (!mountedRef.current || isSigningOut.current) return;
            const newRow = payload.new as any;
            // Only react if subscription_active flipped to false
            if (newRow?.subscription_active === false) {
              console.log('[AUTH] Realtime: subscription_active changed to false, verifying in 3s...');
              setTimeout(async () => {
                if (!mountedRef.current || isSigningOut.current) return;
                try {
                  // Re-fetch profile
                  const { data: freshProfile } = await supabase
                    .from('profiles')
                    .select('subscription_active')
                    .eq('id', userId)
                    .maybeSingle();

                  // Re-fetch company subscription
                  const { data: cuData } = await supabase
                    .from('company_users')
                    .select('company_id')
                    .eq('user_id', userId)
                    .eq('is_active', true)
                    .limit(1)
                    .maybeSingle();

                  let companyActive = false;
                  if (cuData?.company_id) {
                    const { data: subData } = await supabase
                      .from('subscriptions')
                      .select('status, cancel_at_period_end')
                      .eq('company_id', cuData.company_id)
                      .in('status', ['active', 'trialing'])
                      .eq('cancel_at_period_end', false)
                      .limit(1)
                      .maybeSingle();
                    companyActive = !!subData;
                  }

                  const stillInactive = !freshProfile?.subscription_active && !companyActive;
                  if (stillInactive && mountedRef.current && !isSigningOut.current) {
                    console.log('[AUTH] Confirmed subscription inactive after re-check — forcing sign out');
                    forceSignOut('Your subscription is no longer active. Please visit Stripe Billing Portal to manage your subscription.');
                  } else {
                    console.log('[AUTH] Re-check shows subscription still active — ignoring false alarm');
                  }
                } catch (e) {
                  console.error('[AUTH] Error during subscription re-check:', e);
                  // Don't kick out on error — fail open
                }
              }, 3000);
            }
          }
        )
        .subscribe((status) => {
          console.log('[AUTH] Profile subscription status channel:', status);
        });

      // Polling fallback - check every 60 seconds
      pollingRef.current = setInterval(async () => {
        if (!mountedRef.current || isSigningOut.current) return;
        
        const isValid = await checkSessionValid();
        if (!isValid) {
          forceSignOut("You were signed out because your session was revoked.");
        }
      }, 60000);
    };

    const fetchUserProfile = async (userId: string, opts?: { isRefresh?: boolean }) => {
      const isRefresh = !!opts?.isRefresh;
      try {
        const [profileResult, companySubResult, adminResult] = await Promise.all([
          supabase
            .from('profiles')
            .select('first_name, last_name, email, user_type, subscription_active, documents_submitted, shipper_approved, fmcsa_verified, fmcsa_legal_name, fmcsa_dot_number, fmcsa_mc_number, is_demo_user, lr_direct_request_status, lr_direct_requested_at, avatar_url, phone, phone_verified, notification_email, notification_sms_enabled, notification_push_enabled, notification_load_alerts, notification_messages, notification_rate_alerts')
            .eq('id', userId)
            .maybeSingle(),
          supabase
            .from('company_users')
            .select('company_id')
            .eq('user_id', userId)
            .eq('is_active', true)
            .limit(1)
            .maybeSingle(),
          supabase
            .from('admin_roles')
            .select('id')
            .eq('user_id', userId)
            .limit(1),
        ]);

        if (profileResult.data && mountedRef.current) {
          if (isRefresh) {
            // Functional merge: guarantees consumers never observe a `null`
            // profile between "old" and "new" during a refresh, even if any
            // other path transiently touched profile in the same tick.
            setProfile((prev) => ({ ...(prev ?? {} as UserProfile), ...(profileResult.data as UserProfile) }));
          } else {
            setProfile(profileResult.data as UserProfile);
          }
        }

        // Non-blocking admin resolution — failure defaults to non-admin
        let adminStatus = false;
        if (!adminResult.error && adminResult.data && adminResult.data.length > 0) {
          adminStatus = true;
        } else if (adminResult.error) {
          console.warn('Admin role check failed, defaulting to non-admin:', adminResult.error);
        }
        if (mountedRef.current) setIsAdmin(adminStatus);

        // Company subscription — compute the next value FIRST, then set once,
        // so we never flip companySubActive to `false` transiently during a
        // refresh (which would collapse `hasActiveSubscription` and briefly
        // trigger UpgradeOverlay / redirect gates).
        let nextCompanySubActive = false;
        if (companySubResult.data?.company_id) {
          const { data: subData } = await supabase
            .from('subscriptions')
            .select('status, cancel_at_period_end')
            .eq('company_id', companySubResult.data.company_id)
            .in('status', ['active', 'trialing'])
            .eq('cancel_at_period_end', false)
            .limit(1)
            .maybeSingle();
          nextCompanySubActive = !!subData;
        }
        if (mountedRef.current) setCompanySubActive(nextCompanySubActive);
      } catch (error) {
        console.error('Error fetching profile:', error);
      } finally {
        // Only flip the top-level loading flag on initial bootstrap. Refreshes
        // must never toggle it — otherwise ProtectedRoute / UpgradeGate would
        // flash their loading branches every time refreshProfile is called.
        if (!isRefresh && mountedRef.current) setLoading(false);
      }
    };

    fetchUserProfileRef.current = fetchUserProfile;
    // Listen for auth changes
    // Track whether initial session has been bootstrapped to avoid double-init
    const bootstrappedRef = { done: false };

    const handleSession = (event: string, newSession: Session | null, isBootstrap: boolean) => {
      if (!mountedRef.current) return;

      const newUserId = newSession?.user?.id ?? null;
      const prevUserId = currentUserId.current;
      const newSessionId = newSession?.access_token ? extractSessionId(newSession.access_token) : null;
      const prevSessionId = currentSessionId.current;

      // Dedupe: if same user AND same session_id and not a sign-out, skip heavy work.
      // A different session_id means a fresh sign-in (e.g., after password reset / re-login)
      // and MUST re-register the active_sessions row, otherwise the realtime monitor will
      // see a stale session_id in the DB and force-signout the user immediately.
      if (
        newUserId &&
        newUserId === prevUserId &&
        newSessionId &&
        newSessionId === prevSessionId &&
        event !== 'SIGNED_OUT'
      ) {
        setSession(newSession);
        return;
      }

      console.log('Auth event (processing):', event, 'user changed:', prevUserId !== newUserId);
      console.log(`[AUTH-TIMING] handleSession processing: event=${event}, hasUser=${!!newSession?.user}`);
      setSession(newSession);
      setUser(newSession?.user ?? null);

      // Remember Me: persist tokens to localStorage on fresh sign-in and on
      // every silent token refresh, so the saved_at timestamp rolls forward.
      // persistSessionIfRemembered no-ops (and clears) when the pref is false.
      if (newSession && (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED' || event === 'INITIAL_SESSION')) {
        persistSessionIfRemembered(newSession);
      }
      if (event === 'TOKEN_REFRESHED' && newSession?.user) {
        logAuthEvent('token_refreshed', {
          session_id: newSessionId,
          expires_at: newSession.expires_at ?? null,
        }, newSession.user.id);
      }

      if (newSession?.user) {
        setTimeout(() => {
          if (!mountedRef.current) return;
          fetchUserProfile(newSession.user.id);

          if (event === 'SIGNED_IN' || event === 'INITIAL_SESSION' || isBootstrap) {
            registerAndMonitorSession(newSession);

            // Phase 1 telemetry: log clock drift on each sign-in (fire-and-forget, never throws)
            (async () => {
              try {
                const { getClockDriftMs } = await import('@/lib/serverTime');
                const driftMs = await getClockDriftMs();
                await supabase.from('client_clock_drift_logs').insert({
                  user_id: newSession.user.id,
                  drift_seconds: Math.round(driftMs / 1000),
                  user_agent: navigator.userAgent,
                });
              } catch (e) {
                console.warn('[drift-telemetry] log failed (non-fatal):', e);
              }
            })();

            // One-time clock drift check
            if (!driftCheckedRef.current) {
              driftCheckedRef.current = true;
              hasSignificantClockDrift(2).then(async (isDrifted) => {
                if (isDrifted && mountedRef.current) {
                  await getClockDriftMinutes();
                  const driftMessage = `LaneRunner requires your device's clock to match real-world time for security. If you see a clock sync message, your device's date or time is incorrect. Most fixes take less than 30 seconds.\n\nNeed help? Visit /clock-help for step-by-step instructions.`;
                  toast({
                    title: 'Clock Sync Required',
                    description: driftMessage,
                    variant: 'destructive',
                    duration: 60000,
                  });
                  try {
                    sessionStorage.setItem(
                      'lr_auth_flash_v1',
                      JSON.stringify({
                        title: 'Clock Sync Required',
                        description: driftMessage,
                        variant: 'destructive',
                        duration: 30000,
                      })
                    );
                  } catch {}
                }
              }).catch(() => {});
            }
          }
        }, 0);
      } else {
        setProfile(null);
        cleanup();
        currentSessionId.current = null;
        currentUserId.current = null;
        // Explicit SIGNED_OUT (not a forced kick): clear persisted Remember Me.
        if (event === 'SIGNED_OUT') {
          clearPersistedSession();
        }
        setLoading(false);
      }
    };

    // Hot-resume Remember Me recovery. If supabase-js emits SIGNED_OUT
    // MID-SESSION (rotation race, refresh_token_not_found on a
    // background/foreground catch-up, or any silent refresh failure) and
    // it wasn't an intentional user sign-out, attempt to restore from the
    // lr_persisted_session_v1 mirror BEFORE letting the app bounce to
    // /auth. Mirrors the cold-boot recovery lane. A single-flight guard
    // prevents recovery loops if the mirrored refresh token is itself
    // dead.
    const hotResumeRecoveryInFlight = { current: false };
    const attemptHotResumeRecovery = async (trigger: string): Promise<boolean> => {
      if (hotResumeRecoveryInFlight.current) {
        logAuthEvent('hot_resume_recovery', { trigger, outcome: 'skipped_in_flight' }, currentUserId.current);
        return false;
      }
      hotResumeRecoveryInFlight.current = true;
      try {
        const tokens = loadPersistedSessionTokens();
        if (!tokens) {
          logAuthEvent('hot_resume_recovery', { trigger, outcome: 'no_mirror' }, currentUserId.current);
          return false;
        }
        logAuthEvent('hot_resume_recovery', { trigger, outcome: 'attempted' }, currentUserId.current);
        try {
          const { data, error } = await supabase.auth.setSession(tokens);
          if (!error && data.session) {
            console.log('[AUTH] hot-resume Remember Me recovery succeeded');
            logAuthEvent('hot_resume_recovery', { trigger, outcome: 'succeeded' }, data.session.user?.id ?? currentUserId.current);
            // supabase-js will emit SIGNED_IN / TOKEN_REFRESHED which
            // flows back through onAuthStateChange normally.
            return true;
          }
          console.warn('[AUTH] hot-resume recovery: setSession returned error:', error?.message);
          logAuthEvent('hot_resume_recovery', {
            trigger,
            outcome: 'failed',
            error: error?.message ?? 'no session returned',
          }, currentUserId.current);
          clearPersistedSession();
          return false;
        } catch (e) {
          console.warn('[AUTH] hot-resume recovery threw:', e);
          logAuthEvent('hot_resume_recovery', {
            trigger,
            outcome: 'failed',
            error: String((e as Error)?.message ?? e),
          }, currentUserId.current);
          clearPersistedSession();
          return false;
        }
      } finally {
        // Release after a short delay so a burst of SIGNED_OUT events
        // during recovery doesn't retry against the same dead token.
        setTimeout(() => { hotResumeRecoveryInFlight.current = false; }, 2000);
      }
    };

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, newSession) => {
      if (!mountedRef.current) return;
      // If bootstrap already handled the initial session, skip the duplicate INITIAL_SESSION event
      if (event === 'INITIAL_SESSION' && bootstrappedRef.done) return;

      // Silent-drop rescue: mid-session SIGNED_OUT with no explicit user
      // sign-out in flight and a user we thought was signed in. Try the
      // Remember Me mirror before propagating. If recovery succeeds,
      // supabase-js re-emits SIGNED_IN and handleSession consumes that;
      // if it fails, propagate the SIGNED_OUT normally.
      if (
        event === 'SIGNED_OUT' &&
        !isSigningOut.current &&
        currentUserId.current &&
        bootstrappedRef.done
      ) {
        console.log('[AUTH] mid-session SIGNED_OUT observed — attempting Remember Me hot-resume recovery');
        const interceptedUserId = currentUserId.current;
        (async () => {
          const ok = await attemptHotResumeRecovery('signed_out_intercept');
          logAuthEvent('signed_out_intercepted', { recovered: ok }, interceptedUserId);
          if (!ok && mountedRef.current) {
            handleSession('SIGNED_OUT', null, false);
          }
        })();
        return;
      }

      handleSession(event, newSession, false);
    });

    // Native app-resume / web visibility catch-up. autoRefreshToken's
    // internal timer is throttled or paused when the app is backgrounded;
    // on return, force a getSession() so supabase-js issues an immediate
    // refresh instead of waiting for the next network call to surface a
    // dead access token. If getSession returns nothing but we still hold
    // a Remember Me mirror, try the mid-session recovery lane.
    const resumeCatchUp = async (trigger: string) => {
      if (!mountedRef.current || isSigningOut.current) return;
      if (!currentUserId.current) return;
      const backgroundedMs = consumeBackgroundedMs();
      logAuthEvent('app_resume', { trigger, backgrounded_ms: backgroundedMs }, currentUserId.current);
      try {
        const { data: { session: s } } = await supabase.auth.getSession();
        logAuthEvent('resume_catchup', {
          trigger,
          backgrounded_ms: backgroundedMs,
          has_session: !!s,
          has_mirror: !!loadPersistedSessionTokens(),
        }, currentUserId.current);
        if (!s && loadPersistedSessionTokens()) {
          await attemptHotResumeRecovery(`resume:${trigger}`);
        }
      } catch (e) {
        console.warn('[AUTH] resume catch-up failed:', e);
        logAuthEvent('resume_catchup', {
          trigger,
          backgrounded_ms: backgroundedMs,
          error: String((e as Error)?.message ?? e),
        }, currentUserId.current);
      }
    };

    const onBackgrounded = (trigger: string) => {
      if (!currentUserId.current) return;
      markBackgrounded();
      logAuthEvent('app_pause', { trigger }, currentUserId.current);
    };

    const onVisibility = () => {
      if (typeof document === 'undefined') return;
      if (document.visibilityState === 'visible') {
        void resumeCatchUp('visibilitychange');
      } else {
        onBackgrounded('visibilitychange');
      }
    };
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', onVisibility);
    }

    let capacitorResumeCleanup: (() => void) | null = null;
    (async () => {
      try {
        const { isNativePlatform } = await import('@/lib/platform');
        if (!isNativePlatform()) return;
        const { App } = await import('@capacitor/app');
        const resumeHandle = await App.addListener('resume', () => { void resumeCatchUp('capacitor_resume'); });
        const pauseHandle = await App.addListener('pause', () => { onBackgrounded('capacitor_pause'); });
        capacitorResumeCleanup = () => {
          try { resumeHandle.remove(); } catch { /* ignore */ }
          try { pauseHandle.remove(); } catch { /* ignore */ }
        };
      } catch {
        // non-native or plugin unavailable — ignore
      }
    })();


    // Check for existing session — first the Supabase storage adapter
    // (native: @capacitor/preferences; web: sessionStorage), then fall
    // back to the Remember Me tokens mirrored to durable storage.
    //
    // The Remember Me fallback is ALSO used when getSession() succeeds
    // but supabase-js's own refresh returns `refresh_token_not_found` —
    // which happens when a force-close killed the app mid rotation and
    // the sb-auth-token blob points at an already-consumed refresh
    // token. `lr_persisted_session_v1` is a separate mirror written on
    // every TOKEN_REFRESHED, so it's the recovery lane for that race.
    (async () => {
      let bootSession: Session | null = null;
      let bootSource: 'getSession' | 'remember-me-fallback' | 'remember-me-recovery' | 'none' = 'none';

      const { data: { session: existingSession }, error: getSessionError } =
        await supabase.auth.getSession();

      const looksRotationRevoked = (err: unknown): boolean => {
        const msg = String((err as { message?: string } | null)?.message ?? '').toLowerCase();
        return msg.includes('refresh_token_not_found')
          || msg.includes('refresh token not found')
          || msg.includes('invalid refresh token');
      };

      if (existingSession && !getSessionError) {
        bootSession = existingSession;
        bootSource = 'getSession';
      } else if (getSessionError && looksRotationRevoked(getSessionError)) {
        // Rotation race: sb-auth-token was stale. Try the Remember Me
        // mirror before we give up and force a re-login.
        const tokens = loadPersistedSessionTokens();
        if (tokens) {
          try {
            const { data, error } = await supabase.auth.setSession(tokens);
            if (!error && data.session) {
              bootSession = data.session;
              bootSource = 'remember-me-recovery';
            } else {
              clearPersistedSession();
            }
          } catch {
            clearPersistedSession();
          }
        }
      }

      if (!bootSession) {
        const tokens = loadPersistedSessionTokens(); // null if missing or >30d
        if (tokens) {
          try {
            const { data, error } = await supabase.auth.setSession(tokens);
            if (error) {
              // Refresh token revoked / expired server-side — fall through to /auth.
              clearPersistedSession();
            } else {
              bootSession = data.session;
              bootSource = 'remember-me-fallback';
            }
          } catch (e) {
            console.warn('[AUTH] Remember Me restore failed:', e);
            clearPersistedSession();
          }
        }
      }

      console.log(`[AUTH-BOOT] cold-boot session source: ${bootSource}, hasSession: ${!!bootSession}`);
      logAuthEvent('cold_boot', {
        boot_source: bootSource,
        has_session: !!bootSession,
        get_session_error: getSessionError?.message ?? null,
        had_mirror: !!loadPersistedSessionTokens(),
        backgrounded_ms: consumeBackgroundedMs(),
      }, bootSession?.user?.id ?? null);

      if (!mountedRef.current) return;
      bootstrappedRef.done = true;
      handleSession('INITIAL_SESSION', bootSession, true);
    })();

    return () => {
      mountedRef.current = false;

      subscription.unsubscribe();
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onVisibility);
      }
      if (capacitorResumeCleanup) capacitorResumeCleanup();
      cleanup();
    };
  }, []);

  const hasActiveSubscription = !!(profile?.subscription_active || companySubActive);
  const isDemoUser = !!(profile?.is_demo_user);

  return (
    <AuthContext.Provider value={{ user, session, profile, loading, hasActiveSubscription, isDemoUser, isAdmin, refreshProfile }}>
      {children}
    </AuthContext.Provider>
  );
};
